import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useWallet } from "@/context/WalletContext";
import { createApiService } from "@/App";
import { toast } from "sonner";
import Header from "@/components/Header";
import MarketIntelligenceFeed from "@/components/MarketIntelligenceFeed";
import { 
  Bitcoin, 
  Bot, 
  MessageSquare, 
  Heart, 
  Repeat2, 
  Share,
  Send,
  TrendingUp,
  Users,
  Trophy,
  Sparkles,
  MoreHorizontal,
  Image as ImageIcon,
  Hash,
  ArrowLeft,
  X
} from "lucide-react";
import SwapSTXOSHIButton from "@/components/SwapSTXOSHIButton";

const STXOSHI_LOGO = process.env.REACT_APP_LOGO_URL;

export default function Community() {
  const navigate = useNavigate();
  const { isConnected, user, addresses, connectWallet, getAuthHeaders } = useWallet();
  const apiService = createApiService(getAuthHeaders);
  
  const [posts, setPosts] = useState([]);
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState("");
  const [posting, setPosting] = useState(false);
  const [activeTab, setActiveTab] = useState("trending");  // Default to trending
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const fetchPosts = async () => {
    try {
      const [feedRes, trendingRes] = await Promise.all([
        isConnected ? apiService.getFeed() : apiService.getTrending(),
        apiService.getTrending()
      ]);
      setPosts(feedRes.data);
      setTrending(trendingRes.data);
    } catch (err) {
      console.error("Failed to fetch posts:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
    const interval = setInterval(fetchPosts, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  const handlePost = async () => {
    if (!newPost.trim() && !selectedImage) return;
    if (!isConnected) {
      toast.error("Please connect your wallet to post");
      return;
    }
    
    setPosting(true);
    try {
      let imageUrl = null;
      
      // Upload image first if selected
      if (selectedImage) {
        const formData = new FormData();
        formData.append('file', selectedImage);
        
        const uploadRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/image`, {
          method: 'POST',
          headers: {
            'X-STX-Address': addresses?.stx || ''
          },
          body: formData
        });
        
        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          // Use the full URL to the file
          imageUrl = `${process.env.REACT_APP_BACKEND_URL}${uploadData.url}`;
        } else {
          throw new Error('Image upload failed');
        }
      }
      
      // Extract hashtags
      const tags = newPost.match(/#\w+/g)?.map(t => t.slice(1)) || [];
      
      await apiService.createPost({
        content: newPost,
        tags,
        image_url: imageUrl
      });
      
      // Clear form
      setNewPost("");
      removeImage();
      
      toast.success("Posted successfully!");
      fetchPosts();
    } catch (err) {
      console.error('Post error:', err);
      toast.error("Failed to post");
    } finally {
      setPosting(false);
    }
  };

  const handleLike = async (postId) => {
    if (!isConnected) {
      toast.error("Please connect your wallet");
      return;
    }
    try {
      await apiService.likePost(postId);
      fetchPosts();
    } catch (err) {
      if (err.response?.status === 400) {
        await apiService.unlikePost(postId);
        fetchPosts();
      }
    }
  };

  // Copy the post's shareable URL to the clipboard so the user can paste it
  // manually into their X app (or anywhere else). No auto-navigation.
  const sharePost = async (post, e) => {
    if (e) e.stopPropagation();
    if (!post?.id) return;
    const url = `${window.location.origin}/post/${post.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — paste it into your X app");
    } catch {
      // Fallback for insecure contexts / older mobile Safari.
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.top = "-1000px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success("Link copied — paste it into your X app");
      } catch {
        toast.error("Copy failed — link: " + url);
      }
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = (now - date) / 1000;
    
    if (diff < 60) return 'now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  const PostCard = ({ post }) => (
    <Card className="glass-card hover:bg-muted/30 transition-colors cursor-pointer" data-testid={`post-${post.id}`}>
      <CardContent className="p-4">
        <div className="flex gap-3">
          <Avatar 
            className="w-10 h-10 cursor-pointer"
            onClick={() => navigate(`/profile/${post.user?.id}`)}
          >
            <AvatarImage src={post.user?.avatar_url} />
            <AvatarFallback className="bg-primary/20 text-primary font-heading">
              {post.user?.username?.[0]?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span 
                className="font-semibold hover:underline cursor-pointer"
                onClick={() => navigate(`/profile/${post.user?.id}`)}
              >
                {post.user?.username || 'Anonymous'}
              </span>
              {post.user?.is_verified && (
                <Badge className="bg-primary/20 text-primary text-xs px-1">✓</Badge>
              )}
              <span className="text-muted-foreground text-sm">
                @{post.user?.stx_address?.slice(0, 8)}...
              </span>
              <span className="text-muted-foreground text-sm">·</span>
              <span className="text-muted-foreground text-sm">{formatTime(post.created_at)}</span>
            </div>
            
            <p className="text-foreground mb-3 whitespace-pre-wrap break-words">
              {post.content.split(/(#\w+)/g).map((part, i) => 
                part.startsWith('#') ? (
                  <span key={i} className="text-primary hover:underline cursor-pointer">{part}</span>
                ) : part
              )}
            </p>
            
            {post.agent && (
              <div 
                className="bg-muted/50 rounded-lg p-3 mb-3 border border-border hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => navigate(`/agent/${post.agent.id}`)}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Bot className="w-4 h-4 text-primary" />
                  <span className="font-medium text-sm">{post.agent.name}</span>
                  <Badge variant="outline" className="text-xs">
                    Score: {post.agent.trust_score}
                  </Badge>
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="font-mono">{post.agent.sbtc_balance?.toFixed(6)} sBTC</span>
                  <span className={post.agent.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'}>
                    P&L: {post.agent.total_pnl >= 0 ? '+' : ''}{post.agent.total_pnl?.toFixed(6)} sBTC
                  </span>
                </div>
              </div>
            )}
            
            {post.image_url && (
              <img 
                src={post.image_url} 
                alt="Post" 
                className="rounded-lg max-h-80 object-cover mb-3"
              />
            )}
            
            <div className="flex items-center gap-6 text-muted-foreground">
              <button 
                className="flex items-center gap-1 hover:text-primary transition-colors"
                onClick={() => navigate(`/post/${post.id}`)}
              >
                <MessageSquare className="w-4 h-4" />
                <span className="text-sm">{post.replies_count || 0}</span>
              </button>
              <button className="flex items-center gap-1 hover:text-green-400 transition-colors">
                <Repeat2 className="w-4 h-4" />
                <span className="text-sm">{post.reposts_count || 0}</span>
              </button>
              <button 
                className="flex items-center gap-1 hover:text-red-400 transition-colors"
                onClick={(e) => { e.stopPropagation(); handleLike(post.id); }}
              >
                <Heart className="w-4 h-4" />
                <span className="text-sm">{post.likes_count || 0}</span>
              </button>
              <button 
                className="flex items-center gap-1 hover:text-primary transition-colors"
                onClick={(e) => sharePost(post, e)}
                title="Copy shareable link — paste it on X"
                data-testid={`post-share-btn-${post.id}`}
              >
                <Share className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Page Title */}
      {/* Page Title */}
      <div className="glass border-b border-border px-6 py-3">
        <div className="max-w-6xl mx-auto">
          <Badge variant="outline" className="font-mono tracking-wider">COMMUNITY</Badge>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Feed */}
          <div className="lg:col-span-2 space-y-4">
            {/* Compose Post */}
            {isConnected && (
              <Card className="glass-card" data-testid="compose-post">
                <CardContent className="p-4">
                  <div className="flex gap-3">
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={user?.avatar_url} />
                      <AvatarFallback className="bg-primary/20 text-primary">
                        {user?.username?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <Textarea
                        placeholder="Share your trading insights, agent results, or market analysis..."
                        value={newPost}
                        onChange={(e) => setNewPost(e.target.value)}
                        className="bg-transparent border-none resize-none focus:ring-0 p-0 min-h-[80px] text-lg"
                        data-testid="post-input"
                      />
                      
                      {/* Image Preview */}
                      {imagePreview && (
                        <div className="relative mt-3 rounded-lg overflow-hidden">
                          <img src={imagePreview} alt="Preview" className="max-h-64 rounded-lg" />
                          <button
                            onClick={removeImage}
                            className="absolute top-2 right-2 p-1 bg-black/70 rounded-full hover:bg-black"
                          >
                            <X className="w-4 h-4 text-white" />
                          </button>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                        <div className="flex items-center gap-2">
                          {/* Image Upload Button */}
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleImageSelect}
                            accept="image/*"
                            className="hidden"
                          />
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className={`text-muted-foreground hover:text-primary ${imagePreview ? 'text-primary' : ''}`}
                            onClick={() => fileInputRef.current?.click()}
                            title="Add image"
                          >
                            <ImageIcon className="w-4 h-4" />
                          </Button>
                          
                          {/* Link Agent Button */}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-muted-foreground hover:text-primary"
                            onClick={() => {
                              const agentTag = ' @MyAgent';
                              setNewPost(prev => prev + agentTag);
                              toast.info('Link your agents with @AgentName');
                            }}
                            title="Link your agent"
                          >
                            <Bot className="w-4 h-4" />
                          </Button>
                          
                          {/* Hashtag Button */}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-muted-foreground hover:text-primary"
                            onClick={() => {
                              setNewPost(prev => prev + ' #');
                              toast.info('Add hashtags like #DeFi #STX #Yield');
                            }}
                            title="Add hashtag"
                          >
                            <Hash className="w-4 h-4" />
                          </Button>
                        </div>
                        <Button 
                          onClick={handlePost}
                          disabled={!newPost.trim() || posting}
                          className="bg-primary text-primary-foreground"
                          data-testid="post-submit"
                        >
                          {posting ? 'Posting...' : 'Post'}
                          <Send className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Feed Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-2 w-full bg-muted/50">
                <TabsTrigger value="trending" className="font-heading tracking-wide">
                  <Sparkles className="w-4 h-4 mr-2" />
                  TRENDING
                </TabsTrigger>
                <TabsTrigger value="feed" className="font-heading tracking-wide">
                  {isConnected ? 'YOUR FEED' : 'LATEST'}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="trending" className="space-y-4 mt-4">
                {loading ? (
                  <div className="text-center py-12 text-muted-foreground">
                    Loading posts...
                  </div>
                ) : trending.length === 0 ? (
                  <Card className="glass-card">
                    <CardContent className="p-12 text-center">
                      <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                      <h3 className="font-heading text-lg mb-2">No trending posts</h3>
                      <p className="text-muted-foreground">
                        Be the first to create a viral post!
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  trending.map(post => <PostCard key={post.id} post={post} />)
                )}
              </TabsContent>

              <TabsContent value="feed" className="space-y-4 mt-4">
                {loading ? (
                  <div className="text-center py-12 text-muted-foreground">
                    Loading posts...
                  </div>
                ) : posts.length === 0 ? (
                  <Card className="glass-card">
                    <CardContent className="p-12 text-center">
                      <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                      <h3 className="font-heading text-lg mb-2">No posts yet</h3>
                      <p className="text-muted-foreground mb-4">
                        {isConnected 
                          ? "Be the first to share your trading insights!" 
                          : "Connect your wallet to see your personalized feed"}
                      </p>
                      {!isConnected && (
                        <Button onClick={connectWallet}>Connect Wallet</Button>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  posts.map(post => <PostCard key={post.id} post={post} />)
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Market Intelligence Feed */}
            <MarketIntelligenceFeed compact={true} maxItems={6} />
            
            {/* Quick Stats */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg tracking-wider">COMMUNITY STATS</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Total Users</span>
                  <span className="font-mono font-bold">{trending.length > 0 ? '1,234' : '0'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Posts Today</span>
                  <span className="font-mono font-bold">{posts.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Active Bots & Agents</span>
                  <span className="font-mono font-bold">{trending.filter(p => p.agent).length}</span>
                </div>
              </CardContent>
            </Card>

            {/* Trending Tags */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg tracking-wider">TRENDING TAGS</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {['sBTC', 'yield', 'treasury', 'stacks', 'defi', 'bitcoin'].map(tag => (
                    <Badge 
                      key={tag}
                      variant="outline" 
                      className="cursor-pointer hover:bg-primary/20 transition-colors"
                    >
                      #{tag}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Top Agents */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg tracking-wider">TOP AGENTS</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="ghost" 
                  className="w-full justify-start"
                  onClick={() => navigate('/explore')}
                >
                  <Bot className="w-4 h-4 mr-2 text-primary" />
                  <span>Explore All Agents & Bots</span>
                </Button>
              </CardContent>
            </Card>

            {/* Buy STXOSHI CTA */}
            <Card className="glass-card border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-orange-500/10">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Bitcoin className="w-6 h-6 text-amber-400" />
                </div>
                <h3 className="font-heading text-lg mb-2 text-amber-100">Get STXOSHI Tokens</h3>
                <p className="text-sm text-amber-200/70 mb-4">
                  Swap STX for STXOSHI at the current market rate
                </p>
                <SwapSTXOSHIButton className="w-full" />
              </CardContent>
            </Card>

            {/* CTA for non-connected */}
            {!isConnected && (
              <Card className="glass-card border-primary/30">
                <CardContent className="p-6 text-center">
                  <Bitcoin className="w-12 h-12 text-primary mx-auto mb-4" />
                  <h3 className="font-heading text-lg mb-2">Join the Community</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Connect your Xverse or Leather wallet to participate
                  </p>
                  <Button onClick={connectWallet} className="w-full bg-primary text-primary-foreground">
                    Connect Wallet
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
